import { ClockCircleOutlined, LockOutlined, LogoutOutlined, StopOutlined, WarningOutlined } from "@ant-design/icons";
import { AuthBlockReason } from "@services/authEvents";
import { Button, Card, Result, Space, Typography } from "antd";
import styled from "styled-components";

const { Text } = Typography;

// ============================================================================
// Styled Components (matching LoginPage style)
// ============================================================================

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const StyledCard = styled(Card)`
  width: 520px;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  .ant-card-body {
    padding: 40px;
  }
`;

// ============================================================================
// Block Reason Configurations
// ============================================================================

interface BlockReasonConfig {
  icon: React.ReactNode;
  title: string;
  messages: string[];
}

const blockReasonConfigs: Record<AuthBlockReason, BlockReasonConfig> = {
  pending_activation: {
    icon: <ClockCircleOutlined style={{ color: "#faad14" }} />,
    title: "Account Pending Activation",
    messages: ["Your account has not been activated yet.", "Please contact your supervisor to activate your account."],
  },
  unauthorized: {
    icon: <LockOutlined style={{ color: "#ff4d4f" }} />,
    title: "Session Expired",
    messages: ["Your session has expired or is no longer valid.", "Redirecting to login..."],
  },
  forbidden: {
    icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
    title: "Access Denied",
    messages: ["You don't have permission to access this resource.", "Please contact your administrator if you believe this is a mistake."],
  },
  payment_required: {
    icon: <WarningOutlined style={{ color: "#fa8c16" }} />,
    title: "Access Restricted",
    messages: ["Your access to this service is currently restricted.", "Please contact your administrator for assistance."],
  },
  unknown: {
    icon: <WarningOutlined style={{ color: "#faad14" }} />,
    title: "Something Went Wrong",
    messages: ["An unexpected authorization error occurred.", "Please try logging in again or contact support."],
  },
};

// ============================================================================
// Props
// ============================================================================

interface AuthBlockedPageProps {
  reason: AuthBlockReason;
  detail?: string;
  username?: string;
  onLogout: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function AuthBlockedPage({ reason, detail, username, onLogout }: AuthBlockedPageProps) {
  const config = blockReasonConfigs[reason];

  return (
    <PageContainer>
      <StyledCard>
        <Result
          icon={config.icon}
          title={config.title}
          subTitle={
            <Space direction="vertical" size="small" style={{ textAlign: "center" }}>
              {username && (
                <Text>
                  Signed in as <Text strong>{username}</Text>
                </Text>
              )}
              {config.messages.map((msg, i) => (
                <Text key={i} type="secondary">
                  {msg}
                </Text>
              ))}
              {detail && (
                <Text type="secondary" italic>
                  {detail}
                </Text>
              )}
            </Space>
          }
          extra={
            <Button type="primary" danger icon={<LogoutOutlined />} onClick={onLogout}>
              Logout
            </Button>
          }
        />
      </StyledCard>
    </PageContainer>
  );
}
